import React, { useEffect, useRef, useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';

/**
 * HeroSwitchVisual — 히어로 비주얼 ③안 (인터랙티브 스위치)
 *
 * 컨셉: 방문자가 직접 'SWITCHUP 스위치'를 올리면
 *       칙칙하던 사이트(전환율 3.2%)가 눈앞에서 변신(4.8%)합니다.
 *       스위치를 올리는 행위 = Switch Up = 브랜드 각인.
 *
 * 사용법: Home.jsx에서 <HeroVersusVisual /> 자리에 <HeroSwitchVisual />로 교체.
 *
 * 동작:
 *  - 기본 OFF 상태. 스위치는 은은하게 pulse하며 어포던스 제공
 *  - 3.5초간 인터랙션이 없으면 자동으로 켜지고, 켜진 상태로 유지 (A방식)
 *  - prefers-reduced-motion 사용자에겐 ON 상태를 정적으로 표시 (토글은 여전히 동작)
 *  - 전환율 카운터는 rAF 기반 count-up (3.2% ⇄ 4.8%)
 */

// ─────────────────────────────────────────────
// prefers-reduced-motion 감지 훅
// ─────────────────────────────────────────────
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, []);

  return reduced;
};

// ─────────────────────────────────────────────
// 미니 사이트 목업 — 실제 이커머스 상품 페이지처럼
// OFF: 밋밋한 페이지 / ON: 최적화된 페이지 (+ 소셜프루프 팝업)
// ─────────────────────────────────────────────
const MiniSite = ({ on, compact = false }) => {
  return (
    <div className={`relative bg-white rounded-lg border overflow-hidden h-full flex flex-col transition-all duration-700 ${on ? 'border-gray-200 shadow-md' : 'border-gray-200 grayscale opacity-90'
      }`}>
      {/* 쇼핑몰 네비게이션 */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-1.5">
          <div className={`w-3.5 h-3.5 rounded transition-colors duration-700 ${on ? 'bg-blue-600' : 'bg-gray-400'}`}></div>
          <span className="text-[10px] font-black tracking-tight text-gray-800">STORE</span>
        </div>
        <div className="flex gap-2.5 text-[9px] text-gray-400 font-medium">
          <span>신상품</span>
          <span>베스트</span>
          <span>세일</span>
        </div>
      </div>

      {/* 상품 상세: 좌측 이미지 + 우측 정보 */}
      <div className={`flex-1 flex ${compact ? 'p-3 gap-3' : 'p-4 lg:p-6 gap-4 lg:gap-6'}`}>
        {/* 상품 이미지 — 그라데이션 크로스페이드 */}
        <div className={`relative shrink-0 ${compact ? 'w-24 h-24' : 'w-28 h-28 lg:w-44 lg:h-44'} rounded-xl overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-100"></div>
          <div className={`absolute inset-0 bg-gradient-to-br from-blue-100 via-indigo-50 to-white transition-opacity duration-700 ${on ? 'opacity-100' : 'opacity-0'}`}></div>
          <div className={`absolute inset-0 flex items-center justify-center ${compact ? 'text-3xl' : 'text-4xl lg:text-6xl'} transition-transform duration-700 ${on ? 'scale-110' : 'scale-100'}`}>
            🎧
          </div>
          <div className={`absolute top-1.5 left-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded transition-colors duration-700 ${on ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
            }`}>
            BEST
          </div>
        </div>

        {/* 상품 정보 */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className={`${compact ? 'text-xs' : 'text-sm lg:text-lg'} font-bold text-gray-900 leading-snug truncate`}>
            프리미엄 무선 헤드폰
          </div>

          {/* 별점 & 리뷰 — ON 시 별이 살아남 */}
          <div className="flex items-center gap-1 mt-1">
            <span className={`${compact ? 'text-[9px]' : 'text-[10px] lg:text-sm'} tracking-tighter transition-colors duration-700 ${on ? 'text-amber-400' : 'text-gray-300'
              }`}>
              ★★★★★
            </span>
            <span className={`${compact ? 'text-[8px]' : 'text-[9px] lg:text-xs'} text-gray-400 whitespace-nowrap`}>
              4.9 · 리뷰 1,284
            </span>
          </div>

          {/* 가격 */}
          <div className={`${compact ? 'text-sm mt-1.5' : 'text-base lg:text-2xl mt-2'} font-bold text-gray-900 tabular-nums tracking-tight`}>
            ₩89,000
          </div>

          {/* 혜택 칩 — ON 시 컬러 점등 */}
          <div className="flex gap-1 mt-1.5">
            <span className={`${compact ? 'text-[8px]' : 'text-[9px] lg:text-[11px]'} font-medium px-1.5 py-0.5 rounded transition-colors duration-700 ${on ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'
              }`}>
              무료배송
            </span>
            <span className={`${compact ? 'text-[8px]' : 'text-[9px] lg:text-[11px]'} font-medium px-1.5 py-0.5 rounded transition-colors duration-700 ${on ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
              }`}>
              오늘 출발
            </span>
          </div>

          {/* CTA 버튼 — 이 변신의 주인공 */}
          <div className="mt-auto pt-2.5">
            <div className={`flex items-center justify-center gap-1 rounded-lg transition-all duration-700 ${on
              ? 'w-full py-2 lg:py-3 bg-blue-600 shadow-md shadow-blue-500/30'
              : 'w-2/3 py-1.5 lg:py-2 bg-gray-100 border border-gray-300'
              }`}>
              <span className={`${compact ? 'text-[10px]' : 'text-[11px] lg:text-sm'} font-bold transition-colors duration-700 ${on ? 'text-white' : 'text-gray-500'}`}>
                {on ? '지금 구매하기' : '구매'}
              </span>
              {on && <ArrowRight className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3 lg:w-4 lg:h-4'} text-white`} />}
            </div>
          </div>
        </div>
      </div>

      {/* SWITCHUP 팝업 토스트 — ON 시 슬라이드 인 (팝업 기능의 암시) */}
      <div className={`absolute ${compact ? 'bottom-2 left-2' : 'bottom-3 left-3'} flex items-center gap-1.5 bg-white rounded-lg shadow-lg border border-gray-100 ${compact ? 'px-2 py-1' : 'px-2.5 py-1.5'} transition-all duration-500 ${on ? 'opacity-100 translate-y-0 delay-300' : 'opacity-0 translate-y-2 pointer-events-none'
        }`}>
        <span className={compact ? 'text-[10px]' : 'text-xs'}>🔥</span>
        <span className={`${compact ? 'text-[8px]' : 'text-[9px] lg:text-[11px]'} font-medium text-gray-600 whitespace-nowrap`}>
          방금 <b className="text-gray-900">12명</b>이 이 상품을 구매했어요
        </span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// SWITCHUP 컨트롤 패널 — 스위치 + 전환율 카운터
// ─────────────────────────────────────────────
const SwitchPanel = ({ on, rate, interacted, onToggle, horizontal = false }) => {
  return (
    <div className={`bg-white rounded-xl border shadow-lg transition-colors duration-500 ${on ? 'border-blue-200 shadow-blue-500/10' : 'border-gray-200'
      } ${horizontal ? 'p-4' : 'p-5'}`}>

      {/* 브랜드 라벨 */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-black tracking-widest text-gray-900">SWITCHUP</span>
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors duration-500 ${on ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'
          }`}>
          <span className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${on ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'}`}></span>
          {on ? 'ON' : 'OFF'}
        </span>
      </div>

      <div className={horizontal ? 'flex items-center justify-between gap-4' : ''}>
        {/* 토글 스위치 */}
        <div className={horizontal ? '' : 'mb-4'}>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label="SWITCHUP 켜기"
            onClick={onToggle}
            className={`relative w-20 h-11 rounded-full transition-colors duration-500 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${on
              ? 'bg-blue-600 shadow-lg shadow-blue-500/40'
              : 'bg-gray-300'
              }`}
          >
            {/* OFF일 때 어포던스 글로우 */}
            {!on && !interacted && (
              <span className="absolute inset-0 rounded-full ring-4 ring-blue-400/30 animate-pulse motion-reduce:hidden pointer-events-none"></span>
            )}
            <span className={`absolute top-1 w-9 h-9 rounded-full bg-white shadow-md transition-all duration-500 flex items-center justify-center ${on ? 'left-10' : 'left-1'
              }`}>
              {on && <Check className="w-4 h-4 text-blue-600" />}
            </span>
          </button>
          {/* 안내 문구 (OFF + 미인터랙션 시에만) */}
          <div className={`text-[11px] font-medium mt-2 transition-opacity duration-300 ${!on && !interacted ? 'opacity-100 text-blue-600' : 'opacity-0'
            } ${horizontal ? 'hidden' : ''}`}>
            👆 스위치를 올려보세요
          </div>
        </div>

        {/* 전환율 카운터 */}
        <div className={horizontal ? 'text-right' : ''}>
          <div className="text-[10px] text-gray-400 uppercase font-semibold mb-0.5">전환율</div>
          <div className="flex items-baseline gap-1.5 justify-end">
            <span className={`text-3xl font-bold tracking-tight tabular-nums transition-colors duration-500 ${on ? 'text-blue-600' : 'text-gray-400'
              }`}>
              {rate.toFixed(1)}%
            </span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 transition-opacity duration-500 ${on ? 'opacity-100' : 'opacity-0'
              }`}>
              +50%
            </span>
          </div>
          {/* 상태 설명 */}
          <div className={`text-[11px] mt-1 transition-colors duration-500 ${on ? 'text-gray-500' : 'text-gray-400'} ${horizontal ? '' : 'text-left'}`}>
            {on ? 'SWITCHUP이 켜진 사이트' : '현재 사이트'}
          </div>
        </div>
      </div>

      {/* ON 상태 하단 요약 (세로 레이아웃에서만) */}
      {!horizontal && (
        <div className={`mt-4 pt-3 border-t border-gray-100 flex items-center gap-1.5 text-[11px] text-gray-500 transition-opacity duration-500 ${on ? 'opacity-100' : 'opacity-0'
          }`}>
          <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
          신뢰수준 95% · 방문자는 그대로입니다
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────────
const HeroSwitchVisual = () => {
  const prefersReduced = usePrefersReducedMotion();
  const [on, setOn] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [rate, setRate] = useState(3.2);
  const rateRef = useRef(3.2);
  const interactedRef = useRef(false);

  useEffect(() => { rateRef.current = rate; }, [rate]);
  useEffect(() => { interactedRef.current = interacted; }, [interacted]);

  // 모션 최소화 사용자: ON 상태를 정적으로 표시
  useEffect(() => {
    if (prefersReduced && !interactedRef.current) {
      setOn(true);
      setRate(4.8);
    }
  }, [prefersReduced]);

  // 자동 시연 (A방식): 3.5초간 인터랙션이 없으면 켜지고, 켜진 채로 유지
  // — 안 누른 방문자도 결국 '좋은 화면'을 보게 되고, 스위치로 되돌려볼 수 있음
  useEffect(() => {
    if (prefersReduced || interacted) return;
    const t1 = setTimeout(() => setOn(true), 3500);
    return () => clearTimeout(t1);
  }, [prefersReduced, interacted]);

  // 전환율 카운트업 (rAF, ease-out)
  useEffect(() => {
    const target = on ? 4.8 : 3.2;
    if (prefersReduced) {
      setRate(target);
      return;
    }
    const from = rateRef.current;
    if (from === target) return;
    const start = performance.now();
    const duration = 900;
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setRate(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on, prefersReduced]);

  const handleToggle = () => {
    setInteracted(true);
    setOn((v) => !v);
    // TODO: SWITCHUP 이벤트 트래킹 — 히어로 스위치 인게이지먼트 측정 지점
  };

  return (
    <>
      {/* ===== 모바일: 사이트 + 가로형 스위치 패널 세로 스택 ===== */}
      <div className="sm:hidden relative max-w-md mx-auto px-4 pb-4">
        <div className="rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200">
          {/* 미니 브라우저 바 */}
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
            </div>
            <div className="flex-1 text-center">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded bg-white border border-gray-200 text-[10px] text-gray-500 font-mono">
                <span className="text-green-500 mr-1.5">🔒</span>
                yourstore.com
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50/50">
            <MiniSite on={on} compact />
            <div className="mt-3">
              <SwitchPanel on={on} rate={rate} interacted={interacted} onToggle={handleToggle} horizontal />
            </div>
            {/* 모바일용 안내 문구 */}
            <div className={`text-center text-[11px] font-medium text-blue-600 mt-2 transition-opacity duration-300 ${!on && !interacted ? 'opacity-100' : 'opacity-0'
              }`}>
              👆 스위치를 올려보세요
            </div>
          </div>
        </div>

        <div className="absolute -bottom-1 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent z-20 pointer-events-none"></div>
      </div>

      {/* ===== 데스크톱: 3D 틸트 브라우저 프레임 + 사이트/스위치 패널 ===== */}
      <div className="hidden sm:block relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-[-20px]">
        <div
          className="relative rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4"
          style={{
            transform: 'perspective(2000px) rotateX(2deg)',
            transformOrigin: 'top center'
          }}
        >
          <div className="rounded-xl shadow-2xl overflow-hidden bg-white border border-gray-200">

            {/* 브라우저 상단 바 */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 text-center">
                <div className="inline-flex items-center px-3 py-1 rounded bg-white border border-gray-200 text-xs text-gray-500 font-mono">
                  <span className="text-green-500 mr-2">🔒</span>
                  yourstore.com
                </div>
              </div>
            </div>

            {/* 내부: 좌측 사이트 목업 / 우측 SWITCHUP 패널 */}
            <div className={`p-6 lg:p-8 transition-colors duration-700 ${on ? 'bg-blue-50/30' : 'bg-gray-50/80'}`}>
              <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-stretch">
                <MiniSite on={on} />
                <div className="flex flex-col justify-center">
                  <SwitchPanel on={on} rate={rate} interacted={interacted} onToggle={handleToggle} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 그라데이션 페이드 아웃 */}
        <div className="absolute -bottom-1 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-20"></div>
      </div>
    </>
  );
};

export default HeroSwitchVisual;